import { useState, useCallback } from "react";
import { Grid, Icon, Typography, IconButton, Spin } from "~/components/UI";
import BaseCard from "./BaseCard";
import { eTimeInterval } from "./TimeIntervalSelector";
import QueuesCreated from "./Charts/QueuesCreated";
import { useTranslation } from "react-i18next";
import usePermissions from "~/hook/usePermissions";
import { PERMISSION } from "~/api/permissions/enums";

interface QueuesCreatedCardProps {
  title?: string;
  onUpdate: (
    queryParams: any
  ) => Promise<MetricsAPI.MetricsResponceDto | undefined>;
  onUpdateOnline: (queryParams: any) => Promise<number | undefined>;
}

const QueuesCreatedCard = ({
  title,
  onUpdate,
  onUpdateOnline,
}: QueuesCreatedCardProps) => {
  const [loading, setLoading] = useState(false);
  const [latestQuery, setLatestQuery] = useState<MetricsAPI.GetMetricQuery>();
  const [data, setData] = useState<MetricsAPI.MetricsResponceDto>();
  const [onlineData, setOnlineData] = useState<number>();
  const { t } = useTranslation();
  const { hasAll } = usePermissions();

  const getOnlineData = async (query: MetricsAPI.GetMetricQuery) => {
    setLatestQuery(query);
    try {
      const count = await onUpdateOnline({
        ...query,
      });
      setOnlineData(count);
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  const getData = async (query: MetricsAPI.GetMetricQuery) => {
    setLatestQuery(query);
    getOnlineData(query);
    try {
      const data = await onUpdate({
        ...query,
      });
      setData(data);
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  const onRefresh = useCallback(async () => {
    if (!latestQuery) return;
    setLoading(true);
    try {
      await getOnlineData(latestQuery);
    } finally {
      setLoading(false);
    }
  }, [latestQuery]);

  if (!hasAll(PERMISSION.READ_QUEUES_METRICS)) return null;

  return (
    <Grid item sm={12} md={6} xs={3}>
      <BaseCard
        title={title || t("title.queuesCreated")}
        timeIntervals={[eTimeInterval.LAST_MINUTE, eTimeInterval.LAST_HOUR]}
        onUpdate={getData}
        extra={
          <Grid container alignItems="center">
            {data && (
              <>
                <Typography component="span" variant="body2" color="primary">
                  {`${
                    typeof onlineData === "number" ? onlineData : ""
                  } ${"Queues Online"}`}
                </Typography>
                <IconButton disabled={loading} onClick={onRefresh}>
                  <Spin spinning={loading}>
                    <Icon name="Refresh" />
                  </Spin>
                </IconButton>
              </>
            )}
          </Grid>
        }
      >
        {(data) =>
          data ? (
            <QueuesCreated
              period={data.period}
              list={data.list}
              yAxis={t("title.queues")}
            />
          ) : null
        }
      </BaseCard>
    </Grid>
  );
};

export default QueuesCreatedCard;
